import { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import Card from './ui/Card';
import Button from './ui/Button';
import { toast } from './ui/Toast';
import { IconLeaf, IconPencil, IconCheck, IconX } from '@tabler/icons-react';

// UK conversion factors, kg CO2e per litre burned (scope 1, combustion).
// VERIFY against the current DESNZ greenhouse gas conversion factors before
// any customer relies on these for reporting — they are republished annually.
// Held here as one table so an update is a single edit.
const FACTOR_SOURCE = 'DESNZ UK greenhouse gas conversion factors';
const FACTOR_YEAR = 2024;

const FUELS = {
  gas_oil:      { label: 'Red diesel (gas oil)', kgPerLitre: 2.758 },
  diesel:       { label: 'Diesel (road)',        kgPerLitre: 2.512 },
  petrol:       { label: 'Petrol',               kgPerLitre: 2.340 },
  hvo:          { label: 'HVO',                  kgPerLitre: 0.038, note: 'Combustion emissions only; HVO is treated as largely biogenic.' },
  electric:     { label: 'Electric',             kgPerLitre: 0 },
};

// Rough starting points until the canonical figure arrives from HQ. These are
// assumptions, not measurements — the point is to give the user something to
// correct rather than a blank field.
const DEFAULT_LITRES_PER_HOUR = {
  tractor: 12,
  telehandler: 9,
  excavator: 10,
  loader: 11,
  mower: 5,
  sprayer: 8,
  harvester: 30,
  utv: 3,
};

const fmt = (n, dp = 0) =>
  n === null || n === undefined || isNaN(n)
    ? '—'
    : n.toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });

export default function MachineCarbon({ machineId, machine, checks = [], onSaved }) {
  const { orgId, role } = useAuth();
  const canEdit = role === 'org_admin' || role === 'manager';

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [litres, setLitres] = useState('');
  const [fuelType, setFuelType] = useState('gas_oil');

  const defaultRate = DEFAULT_LITRES_PER_HOUR[machine?.subCategory] || null;
  const rate = machine?.fuelLitresPerHour ?? defaultRate;
  const fuel = FUELS[machine?.fuelType || 'gas_oil'];
  const isAssumed = machine?.fuelLitresPerHour === undefined || machine?.fuelLitresPerHour === null;

  useEffect(() => {
    setLitres(machine?.fuelLitresPerHour ?? defaultRate ?? '');
    setFuelType(machine?.fuelType || 'gas_oil');
  }, [machine?.fuelLitresPerHour, machine?.fuelType, defaultRate]);

  // Hours worked in the last twelve months, from the spread of engine-hour
  // readings on submitted checks. Falls back to nothing rather than guessing.
  const hoursLastYear = useMemo(() => {
    const cutoff = Date.now() - 365 * 86400000;
    const readings = checks
      .map(c => ({
        at: c.submittedAt?.toDate ? c.submittedAt.toDate().getTime() : null,
        hours: typeof c.engineHours === 'number' ? c.engineHours : null,
      }))
      .filter(r => r.at && r.hours !== null && r.at >= cutoff)
      .sort((a, b) => a.at - b.at);

    if (readings.length < 2) return null;
    const delta = readings[readings.length - 1].hours - readings[0].hours;
    return delta > 0 ? delta : null;
  }, [checks]);

  const totalHours = typeof machine?.currentHours === 'number' ? machine.currentHours : null;

  const lifetimeKg = rate && totalHours !== null ? rate * totalHours * fuel.kgPerLitre : null;
  const yearKg = rate && hoursLastYear !== null ? rate * hoursLastYear * fuel.kgPerLitre : null;
  const perHourKg = rate ? rate * fuel.kgPerLitre : null;

  const handleSave = async () => {
    const parsed = parseFloat(litres);
    if (isNaN(parsed) || parsed < 0) { toast.error('Enter a fuel consumption figure'); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'machines', machineId), {
        fuelLitresPerHour: parsed,
        fuelType,
        updatedAt: serverTimestamp(),
      });
      onSaved?.({ fuelLitresPerHour: parsed, fuelType });
      setEditing(false);
      toast.success('Fuel figures updated');
    } catch (err) {
      console.error(err);
      toast.error('Could not save the fuel figures');
    }
    setSaving(false);
  };

  const Stat = ({ label, value, unit, sub }) => (
    <div style={{ flex: 1, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{label}</div>
      <div style={{ fontSize: 17, color: 'var(--color-text-primary)', marginTop: 3 }}>
        {value}
        {value !== '—' && <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginLeft: 4 }}>{unit}</span>}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconLeaf size={15} color="var(--color-success)" stroke={1.5} />
          <div style={{ fontSize: 14, fontWeight: 400 }}>Estimated carbon</div>
        </div>
        {canEdit && !editing && (
          <Button size="sm" variant="ghost" icon={<IconPencil size={13} stroke={1.5} />} onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>

      {editing ? (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                Fuel use (litres per hour)
              </label>
              <input
                type="number" min="0" step="0.1" value={litres}
                onChange={e => setLitres(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                Fuel type
              </label>
              <select
                value={fuelType} onChange={e => setFuelType(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', padding: '7px 8px' }}
              >
                {Object.entries(FUELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" onClick={handleSave} disabled={saving} loading={saving} icon={<IconCheck size={13} stroke={1.5} />}>
              Save
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)} icon={<IconX size={13} stroke={1.5} />}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <Stat label="Last 12 months" value={yearKg === null ? '—' : fmt(yearKg / 1000, 2)} unit="t CO2e"
                  sub={hoursLastYear === null ? 'Not enough check data' : `${fmt(hoursLastYear)} h worked`} />
            <Stat label="Lifetime" value={lifetimeKg === null ? '—' : fmt(lifetimeKg / 1000, 2)} unit="t CO2e"
                  sub={totalHours === null ? 'No hours recorded' : `${fmt(totalHours)} h total`} />
            <Stat label="Per hour" value={perHourKg === null ? '—' : fmt(perHourKg, 1)} unit="kg CO2e"
                  sub={fuel.label} />
          </div>

          {!rate && (
            <div style={{ fontSize: 12, color: 'var(--color-warning)', marginBottom: 8 }}>
              No fuel consumption figure for this machine. Add one to see an estimate.
            </div>
          )}

          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', lineHeight: 1.6 }}>
            Estimated from engine hours at {rate ? `${rate} litres per hour` : 'an unknown rate'}
            {isAssumed && rate ? ' (a category assumption — edit to set the real figure)' : ''}, using{' '}
            {FACTOR_SOURCE} {FACTOR_YEAR}. This is an estimate from recorded hours, not measured fuel use.
            {fuel.note ? ' ' + fuel.note : ''}
          </div>
        </>
      )}
    </Card>
  );
}
